// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { maskIban } from '@client/banks/utils/bank-utils';

/** True when the account has a non-empty code number that can be used for linking. */
export const hasLinkableAccountIban = (
  iban: string | null | undefined,
): iban is string => typeof iban === 'string' && iban.trim() !== '';

/** Normalize API / unknown IBAN into form state (`''` when empty or missing). */
export const toFormOptionalIban = (iban: string | null | undefined): string =>
  hasLinkableAccountIban(iban) ? iban.trim() : '';

/** Normalize form IBAN to API `null` when empty / unlinked. */
export const toWireOptionalIban = (
  iban: string | null | undefined,
): string | null => (hasLinkableAccountIban(iban) ? iban.trim() : null);

/** Masked IBAN for list meta lines (last four digits only, e.g. `•••• 5180`). */
export const formatAccountIbanReference = (
  iban: string | null | undefined,
): string => {
  if (!hasLinkableAccountIban(iban)) {
    return '-';
  }

  const masked = maskIban(iban, 'last-only');

  return masked === '-' ? '-' : masked;
};

/**
 * List reference for a liquidity account: masked code number when present,
 * otherwise the account name so rows without IBAN stay distinguishable.
 */
export const formatAccountListReference = (account: {
  iban: string;
  name: string;
}): string => {
  if (hasLinkableAccountIban(account.iban)) {
    return formatAccountIbanReference(account.iban);
  }

  const name = account.name.trim();

  return name === '' ? '-' : name;
};
